import gql from 'graphql-tag'
import type MapFeature from '@/lib/map-feature'
import type { MapMouseEvent } from '@/lib/map-x/webmap-viewer-types'
import type { RouteParamsRaw } from 'vue-router'
import router from '@/router'
import {
  regionIdFromUrn,
  fishingAreaIdFromUrn,
} from '@/fish/lib/graph/utilities'
import { FISH_LAYERS } from '@/fish/lib/enums'

import Geographic from '@/lib/geographic'
import {
  parseFeatureIdentifier,
  isRegionBadge,
} from '@/lib/feature-identification'
import { getApolloClient } from '@/lib/apollo-client'
import { CARD_ORIGINS } from '@/lib/enums-marketing'
import { warn } from '@/lib/logger'

import {
  setQueryCoord,
  setFeatureSelected,
  setQueryMarkerCoord,
} from '@/services/map-service'

import routeNames from '@/fish/router/constants'

/**
 * Route to the correct Waterbody card
 */
export const handleWaterbodyClick = async (
  features: MapFeature[],
  event: MapMouseEvent
) => {
  let routeName
  const routeParams: RouteParamsRaw = {}
  const coord = event.lngLat ? Geographic.fromMapbox(event.lngLat) : null
  if (coord) {
    setQueryCoord(coord)
    setQueryMarkerCoord(coord)
  }

  // There are different hierarchies of waterbody features and cards
  // They are routed based on map features and properties present
  const waterbody = features.find(
    (feat) => feat.sourceLayer === FISH_LAYERS.WATERBODY_SOURCE
  )
  const region = features.find(
    (feat) => feat.sourceLayer === FISH_LAYERS.REGION
  )

  // Clicked waterbody contains a region
  if (region) {
    const featureUrn = parseFeatureIdentifier(region)
    const mapDisplay = region?.properties?.['data:map display']

    try {
      const guide = await regionIdFromUrn(featureUrn)
      if (!guide) {
        warn('Logger', 'Did not parse region from URN')
        return
      }
      // Sub-region (1 waterbody, contains multiple regions)
      if (mapDisplay && waterbody) {
        routeName = routeNames.REGION_WATERBODY_OVERVIEW
        routeParams.regionId = guide.id
        routeParams.waterbodyId = waterbody.id
      }
      // Region only (no waterbody, 1 region)
      else if (mapDisplay && !waterbody) {
        routeName = routeNames.REGION_OVERVIEW
        routeParams.regionId = guide.id
      }
      // Waterbody (1 waterbody, 1 region)
      else if (waterbody) {
        routeName = routeNames.WATERBODY
        routeParams.id = waterbody.id
      }
    } catch (error) {
      routeName = routeNames.FISH_MAP
      warn('Logger', 'Error fetching region guide:', error)
    }
  }
  // Clicked waterbody does not contain a region (1 waterbody, no region)
  else if (!region && waterbody) {
    routeParams.id = waterbody.id
    routeName = routeNames.WATERBODY
  }

  if (routeName) {
    router
      .push({
        name: routeName,
        params: routeParams,
        state: { origin: CARD_ORIGINS.MAP_TAP },
      })
      .catch(() => {})
  }
}

/**
 * Route to the correct Fishing Area card
 */
export const handleFishingAreaClick = async (features: MapFeature[]) => {
  const feature = features[0]

  let routeName = null

  if (feature) {
    const featureUrn = parseFeatureIdentifier(feature)
    const area = await fishingAreaIdFromUrn(featureUrn)
    if (area) {
      routeName = routeNames.FISHING_AREA
      setFeatureSelected({
        id: feature.id,
        source: feature?.source,
        sourceLayer: feature?.sourceLayer,
      })
      if (routeName && area.id) {
        router
          .push({
            name: routeName,
            params: { areaId: area.id },
            state: { mapTap: true, origin: CARD_ORIGINS.MAP_TAP },
          })
          .catch(() => {})
      }
    }
  }
}

/**
 * Route to the correct Water Access card
 */
export const handleWaterAccessFeatureClick = async (features: MapFeature[]) => {
  const feature = features[0]
  let routeName = null
  const routeParams: RouteParamsRaw = {}

  if (feature) {
    const siteQuery = () => {
      return gql`
        query SiteById($filter: WaterAccessSiteFilter) {
          waterAccessSites(filter: $filter) {
            id
          }
        }
      `
    }

    const featureUrn = parseFeatureIdentifier(feature)
    const filter = {
      filter: {
        identifiers: {
          intersects: featureUrn,
        },
      },
    }
    const client = getApolloClient()
    const response = await client.query({
      query: siteQuery(),
      variables: filter,
    })
    routeParams.id = response.data.waterAccessSites[0].id
    routeName = routeNames.WATER_ACCESS_SITE
    if (routeName) {
      router
        .push({
          name: routeName,
          params: routeParams,
          state: { mapTap: true, origin: CARD_ORIGINS.MAP_TAP },
        })
        .catch(() => {})
    }
  }
}

/**
 * Route to the correct Waterbody card via filter badges
 */
export const handleWaterbodyBadgesClicked = async (
  badgeFeatures: MapFeature[],
  waterbodyFeatures: MapFeature[],
  event: MapMouseEvent
) => {
  let routeName
  const badgeFeature = badgeFeatures[0]
  const waterbody = waterbodyFeatures.find(
    (feat) => feat.sourceLayer === FISH_LAYERS.WATERBODY_SOURCE
  )
  const region = waterbodyFeatures.find(
    (feat) => feat.sourceLayer === FISH_LAYERS.REGION
  )
  const routeParams: RouteParamsRaw = {}
  if (badgeFeature) {
    const coord = event.lngLat ? Geographic.fromMapbox(event.lngLat) : null
    if (coord) {
      setQueryCoord(coord)
    }
    if (isRegionBadge(badgeFeature)) {
      setFeatureSelected({
        id: badgeFeature.id,
        source: FISH_LAYERS.WATER_ACCESS_SOURCE,
        sourceLayer: FISH_LAYERS.REGION_LABEL,
      })
    } else {
      setFeatureSelected({
        id: badgeFeature.id,
        source: FISH_LAYERS.WATERBODY_LAYER,
        sourceLayer: FISH_LAYERS.POI_SOURCE,
      })
    }

    // Clicked waterbody contains a region
    if (region) {
      const featureUrn = parseFeatureIdentifier(region)
      const mapDisplay = region?.properties?.['data:map display']

      try {
        const guide = await regionIdFromUrn(featureUrn)
        if (!guide) {
          warn('Logger', 'Did not parse region from URN')
          return
        }
        // Sub-region (1 waterbody, contains multiple regions)
        if (mapDisplay && waterbody) {
          routeName = routeNames.REGION_WATERBODY_OVERVIEW
          routeParams.regionId = guide.id
          routeParams.waterbodyId = waterbody.id
        }
        // Region only (no waterbody, 1 region)
        else if (mapDisplay && !waterbody) {
          routeName = routeNames.REGION_OVERVIEW
          routeParams.regionId = guide.id
        }
        // Waterbody (1 waterbody, 1 region)
        else if (waterbody) {
          routeName = routeNames.WATERBODY
          routeParams.id = waterbody.id
        }
      } catch (error) {
        routeName = routeNames.FISH_MAP
        warn('Logger', 'Error fetching region guide:', error)
      }
    }
    // Clicked waterbody does not contain a region (1 waterbody, no region)
    else if (!region && waterbody) {
      routeParams.id = waterbody.id
      routeName = routeNames.WATERBODY
    }

    if (routeName) {
      router
        .push({
          name: routeName,
          params: routeParams,
          state: { origin: CARD_ORIGINS.MAP_TAP },
        })
        .catch(() => {})
    }
  }
}

/**
 * Route to query overview card for events
 */
export const handleEventClick = (
  features: MapFeature[],
  event: MapMouseEvent
) => {
  const feature = features[0]
  const coord = event.lngLat ? Geographic.fromMapbox(event.lngLat) : null
  if (feature && coord) {
    setFeatureSelected({
      id: feature.id,
      source: feature?.source,
      sourceLayer: feature?.sourceLayer,
    })
    router
      .push({
        name: routeNames.QUERY_FISH,
        params: {
          coord: `${coord.latitude},${coord.longitude}`,
        },
        state: { mapTap: true, origin: CARD_ORIGINS.MAP_TAP, hideMarker: true },
      })
      .catch(() => {})
  }
}

/**
 * Route to catch polygon card
 */
export const handleCatchPolygonClick = (event: MapMouseEvent) => {
  const coord = event.lngLat ? Geographic.fromMapbox(event.lngLat) : null
  if (coord) {
    router
      .push({
        name: routeNames.POLYGON,
        params: {
          coord: `${coord.latitude},${coord.longitude}`,
        },
        state: { mapTap: true },
      })
      .catch(() => {})
  }
}
